import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../services/api'
import type { ConfidentialityPolicyConfig, ConfidentialityLevel } from '../types/confidentiality'
import './SettingsConfidentiality.css'

const CONFIDENTIALITY_LEVELS: ConfidentialityLevel[] = [
  'public',
  'restricted',
  'confidential',
  'highly-confidential',
]

const FIELD_LABELS: Record<string, string> = {
  medications: 'Medications',
  conditions: 'Conditions',
  'sensitive-documents': 'Sensitive Documents',
  'lab-results': 'Lab Results',
  'vital-signs': 'Vital Signs',
  allergies: 'Allergies',
  'family-history': 'Family History',
}

export function SettingsConfidentiality() {
  const queryClient = useQueryClient()
  const { hasPermission } = useAuth()
  const [formData, setFormData] = useState<ConfidentialityPolicyConfig | null>(null)

  const { data: policy, isLoading, error } = useQuery({
    queryKey: ['confidentiality-policy'],
    queryFn: async () => {
      const response = await api.get<ConfidentialityPolicyConfig>('/role-permissions/confidentiality-policy')
      return response.data
    },
  })

  useEffect(() => {
    if (policy) {
      setFormData(policy)
    } else {
      // Set default policy if none exists
      setFormData({
        fieldPolicies: {},
        defaultConfidentialityLevel: 'restricted',
        allowEmergencyOverride: true,
      })
    }
  }, [policy])

  const updateMutation = useMutation({
    mutationFn: async (data: ConfidentialityPolicyConfig) => {
      const response = await api.post<ConfidentialityPolicyConfig>('/role-permissions/confidentiality-policy', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['confidentiality-policy'] })
      alert('Confidentiality policy updated successfully')
    },
  })

  const handleFieldPolicyChange = (field: string, level: ConfidentialityLevel) => {
    if (!formData) return

    setFormData({
      ...formData,
      fieldPolicies: {
        ...(formData.fieldPolicies || {}),
        [field]: {
          field,
          defaultLevel: level,
          requiredPermissions: getRequiredPermissionsForLevel(level),
          description: `Default confidentiality level for ${FIELD_LABELS[field] || field}`,
        },
      },
    })
  }

  const handleDefaultLevelChange = (level: ConfidentialityLevel) => {
    if (!formData) return
    setFormData({ ...formData, defaultConfidentialityLevel: level })
  }

  const handleEmergencyOverrideChange = (allow: boolean) => {
    if (!formData) return
    setFormData({ ...formData, allowEmergencyOverride: allow })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData) return

    try {
      await updateMutation.mutateAsync(formData)
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'Failed to update confidentiality policy')
    }
  }

  const getRequiredPermissionsForLevel = (level: ConfidentialityLevel): string[] => {
    switch (level) {
      case 'public':
        return ['patients:read']
      case 'restricted':
        return ['patients:read']
      case 'confidential':
        return ['patients:read:full-access']
      case 'highly-confidential':
        return ['patients:read:full-access']
      default:
        return ['patients:read']
    }
  }

  const canEdit = hasPermission('role-permissions:write') || hasPermission('*')

  if (!hasPermission('role-permissions:read') && !hasPermission('*')) {
    return (
      <div className="settings-content" role="main">
        <div className="error" role="alert">
          <h2>Access Denied</h2>
          <p>You don't have permission to view confidentiality settings.</p>
        </div>
      </div>
    )
  }

  if (isLoading && !formData) {
    return (
      <div className="settings-content" role="main">
        <div className="loading" role="status" aria-live="polite">
          Loading confidentiality policy...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="settings-content" role="main">
        <div className="error" role="alert">
          <h2>Error</h2>
          <p>Failed to load confidentiality policy. Please try again.</p>
        </div>
      </div>
    )
  }

  if (!formData) {
    return (
      <div className="settings-content" role="main">
        <div className="error" role="alert">
          <h2>Error</h2>
          <p>No confidentiality policy data available.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="settings-content settings-confidentiality" role="main">
      <div className="settings-header">
        <div>
          <h1>Confidentiality Policy</h1>
          <p className="settings-subtitle">
            Configure default confidentiality levels for patient data fields and organizational policies.
          </p>
        </div>
      </div>

      {!formData ? (
        <div className="loading" role="status" aria-live="polite">
          Loading confidentiality policy...
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="confidentiality-policy-form">
          <section className="settings-section">
            <h2>Default Confidentiality Level</h2>
            <p className="section-description">
              Set the default confidentiality level for patient data fields that don't have a specific policy.
            </p>

            <div className="form-group">
              <label htmlFor="default-confidentiality-level">Default Level</label>
              <select
                id="default-confidentiality-level"
                value={formData.defaultConfidentialityLevel}
                onChange={(e) => handleDefaultLevelChange(e.target.value as ConfidentialityLevel)}
                disabled={!canEdit}
                className="form-control"
              >
              {CONFIDENTIALITY_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {level.charAt(0).toUpperCase() + level.slice(1).replace('-', ' ')}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section className="settings-section">
          <h2>Field-Specific Policies</h2>
          <p className="section-description">
            Configure confidentiality levels for specific patient data fields. These override the default level.
          </p>

          <div className="field-policies-grid">
            {Object.keys(FIELD_LABELS).map((field) => {
              const fieldPolicy = formData.fieldPolicies?.[field]
              const currentLevel = fieldPolicy?.defaultLevel || formData.defaultConfidentialityLevel

              return (
                <div key={field} className="field-policy-item">
                  <label htmlFor={`field-${field}`} className="field-policy-label">
                    {FIELD_LABELS[field]}
                  </label>
                  <select
                    id={`field-${field}`}
                    value={currentLevel}
                    onChange={(e) => handleFieldPolicyChange(field, e.target.value as ConfidentialityLevel)}
                    disabled={!canEdit}
                    className="form-control"
                  >
                    {CONFIDENTIALITY_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {level.charAt(0).toUpperCase() + level.slice(1).replace('-', ' ')}
                      </option>
                    ))}
                  </select>
                  {fieldPolicy && (
                    <button
                      type="button"
                      className="btn-link btn-small"
                      onClick={() => {
                        if (!formData) return
                        const { [field]: removed, ...rest } = formData.fieldPolicies
                        setFormData({ ...formData, fieldPolicies: rest })
                      }}
                      disabled={!canEdit}
                    >
                      Reset to default
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </section>

        <section className="settings-section">
          <h2>Emergency Override</h2>
          <p className="section-description">
            Allow authorized users to request emergency access to restricted patient data.
          </p>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.allowEmergencyOverride}
                onChange={(e) => handleEmergencyOverrideChange(e.target.checked)}
                disabled={!canEdit}
              />
              <span>Allow emergency access override requests</span>
            </label>
            <p className="field-hint">
              When enabled, users with the appropriate permissions can request temporary access to restricted data in emergency situations.
            </p>
          </div>
        </section>

        {canEdit && (
          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Policy'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                if (policy) {
                  setFormData(policy)
                }
              }}
              disabled={updateMutation.isPending}
            >
              Reset
            </button>
          </div>
        )}
      </form>
      )}

      {/* Information Section */}
      <section className="settings-info-section">
        <h2>Understanding Confidentiality Levels</h2>
        <div className="info-content">
          <div className="info-subsection">
            <h3>Confidentiality Levels</h3>
            <p>
              Confidentiality levels define how sensitive patient data is, from least to most restrictive:
            </p>
            <ul className="info-list">
              <li>
                <strong>Public</strong> - Generally accessible information. Requires: <code>patients:read</code>
              </li>
              <li>
                <strong>Restricted</strong> - Limited access, requires specific permission. Requires: <code>patients:read</code> + field-level permission (e.g., <code>patients:read:medications</code>)
              </li>
              <li>
                <strong>Confidential</strong> - Sensitive information, requires elevated permissions. Requires: <code>patients:read:full-access</code> (always required, even if you have field-level permission)
              </li>
              <li>
                <strong>Highly Confidential</strong> - Most sensitive information. Requires: <code>patients:read:full-access</code> (always required)
              </li>
            </ul>
          </div>

          <div className="info-subsection">
            <h3>How Confidentiality Relates to Permissions</h3>
            <p>
              The system uses a layered access control model that combines permissions and confidentiality levels:
            </p>
            <div className="permission-hierarchy">
              <div className="hierarchy-item">
                <code>patients:read</code>
                <ul>
                  <li>Basic patient info (public/restricted fields that aren't sensitive)</li>
                  <li>NOT sufficient for sensitive fields (medications, conditions, vital-signs, etc.)</li>
                  <li>NOT sufficient for confidential/highly-confidential data</li>
                </ul>
              </div>
              <div className="hierarchy-item">
                <code>patients:read:{'{field}'}</code>
                <ul>
                  <li>Access to specific field (e.g., <code>patients:read:medications</code>)</li>
                  <li>Works for public/restricted levels</li>
                  <li>If field is confidential/highly-confidential, ALSO need <code>patients:read:full-access</code></li>
                </ul>
              </div>
              <div className="hierarchy-item">
                <code>patients:read:full-access</code>
                <ul>
                  <li>Access to ALL patient data</li>
                  <li>Required for confidential/highly-confidential levels</li>
                  <li>Bypasses confidentiality restrictions</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="info-subsection">
            <h3>Example Scenarios</h3>
            <div className="scenario-list">
              <div className="scenario-item">
                <strong>Receptionist viewing Vital Signs:</strong>
                <ul>
                  <li>Receptionist has: <code>patients:read</code></li>
                  <li>Vital Signs confidentiality: <code>restricted</code></li>
                  <li>Vital Signs is a sensitive field</li>
                  <li><strong>Result:</strong> Access denied (needs <code>patients:read:vital-signs</code> or <code>patients:read:full-access</code>)</li>
                </ul>
              </div>
              <div className="scenario-item">
                <strong>Doctor viewing Confidential Mental Health Record:</strong>
                <ul>
                  <li>Doctor has: <code>patients:read:conditions</code></li>
                  <li>Condition confidentiality: <code>confidential</code></li>
                  <li><strong>Result:</strong> Access denied (needs <code>patients:read:full-access</code> even though they have field-level permission)</li>
                </ul>
              </div>
              <div className="scenario-item">
                <strong>Admin viewing Highly Confidential HIV Status:</strong>
                <ul>
                  <li>Admin has: <code>patients:read:full-access</code></li>
                  <li>HIV status confidentiality: <code>highly-confidential</code></li>
                  <li><strong>Result:</strong> Access granted (full-access bypasses confidentiality restrictions)</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="info-subsection">
            <h3>Key Points</h3>
            <ul className="info-list">
              <li>Confidentiality levels define <strong>data sensitivity</strong> (public → restricted → confidential → highly-confidential)</li>
              <li>Permissions define <strong>user capabilities</strong> (<code>patients:read</code> → <code>patients:read:{'{field}'}</code> → <code>patients:read:full-access</code>)</li>
              <li>The system <strong>combines both</strong>: even with field-level permission, confidential/highly-confidential data requires <code>patients:read:full-access</code></li>
              <li>Sensitive fields (medications, conditions, vital-signs, lab-results, allergies, family-history) require explicit permissions even if confidentiality is only <code>restricted</code></li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  )
}

export default SettingsConfidentiality
